import React, { useMemo, useState } from 'react';
import { Card, Input, Select, Space, Tag, Typography } from 'antd';
import StandardTable, { createActionColumn } from '../../../../components/common/StandardTable';
import { useNavigate } from 'react-router-dom';
import { useGetQaqcPipeInspector, useGetCompanies, useGetPersonnel } from '../../../../hooks/genericHooks';
import type { QaqcPipeInspector, Company, VwPersonnel } from '../../../../types';
import Button from '../../../../components/common/Button';

const { Title } = Typography;

export default function QaqcPipeInspectorList() {
  const navigate = useNavigate();
  const { data: inspResp, isLoading } = useGetQaqcPipeInspector();
  const { data: companiesResp } = useGetCompanies();
  const { data: personnelResp } = useGetPersonnel();

  const [search, setSearch] = useState<string>('');
  const [companyId, setCompanyId] = useState<number | undefined>();
  const [status, setStatus] = useState<'active' | 'inactive' | 'terminated' | undefined>();

  const companies = (companiesResp?.data as unknown as Company[]) || [];
  const personnel = (personnelResp?.data as unknown as VwPersonnel[]) || [];
  const data: QaqcPipeInspector[] = (inspResp?.data as unknown as QaqcPipeInspector[]) || [];

  const companyOptions = useMemo(() => {
    const ids = new Set<number>((data || []).map(r => r.company_id!).filter(Boolean) as number[]);
    const opts = Array.from(ids).map(id => {
      const c = companies.find(cc => cc.id === id);
      return { value: id, label: c ? c.name : String(id) };
    });
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  }, [data, companies]);

  const filtered = useMemo(() => {
    const tokens = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return data.filter(r => {
      if (companyId && r.company_id !== companyId) return false;
      if (status && r.employment_status !== status) return false;
      if (tokens.length) {
        const pers = personnel.find(p => p.pers_id === r.pers_id);
        const full = `${pers?.first_name || ''} ${pers?.last_name || ''}`.trim();
        const hay = `${r.inspector_name} ${full}`.toLowerCase();
        if (!tokens.every(t => hay.includes(t))) return false;
      }
      return true;
    });
  }, [data, search, companyId, status, personnel]);

  const columns: any[] = [
    { title: 'Inspector', dataIndex: 'inspector_name', key: 'inspector_name', width: 60 },
    { title: 'Personnel', key: 'personnel', width: 160, render: (_: any, rec: QaqcPipeInspector) => {
        const p = personnel.find(pp => pp.pers_id === rec.pers_id);
        return p ? `${p.first_name} ${p.last_name}` : rec.pers_id;
      }
    },
    { title: 'Company', key: 'company', width: 160, render: (_: any, rec: QaqcPipeInspector) => {
        const c = companies.find(cc => cc.id === rec.company_id);
        return c ? c.name : rec.company_id;
      }
    },
    { title: 'Status', dataIndex: 'employment_status', key: 'employment_status', width: 80, render: (s: QaqcPipeInspector['employment_status']) => (
        <Tag color={s==='active'?'green': s==='inactive'?'gold':'red'}>{s}</Tag>
      )
    },
    createActionColumn<QaqcPipeInspector>({
      title: 'Actions',
      width: 160,
      onView: (rec) => {
        const id = (rec as any).id ?? (rec as any).inspector_id;
        if (id != null) navigate(`/qaqc/piping/tools/inspectors/view/${id}`, { state: { record: rec } });
      },
      onEdit: (rec) => {
        const id = (rec as any).id ?? (rec as any).inspector_id;
        if (id != null) navigate(`/qaqc/piping/tools/inspectors/edit/${id}`, { state: { record: rec } });
      },
      showView: true,
      showDelete: false,
    })
  ];
  return (
    <Card title={<Title level={4} style={{ margin: 0 }}>Inspectors</Title>} size="small">
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        <Space wrap>
          <Input.Search
            allowClear
            placeholder="Search inspector or personnel"
            style={{ width: 260 }}
            onSearch={setSearch}
            onChange={(e) => setSearch(e.target.value)}
            value={search}
          />
          <Select
            allowClear
            showSearch
            style={{ width: 220 }}
            placeholder="Filter company"
            options={companyOptions}
            optionFilterProp="label"
            filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
            value={companyId}
            onChange={(v) => setCompanyId(v as number | undefined)}
            onClear={() => setCompanyId(undefined)}
          />
          <Select
            allowClear
            style={{ width: 180 }}
            placeholder="Filter status"
            options={[
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
              { value: 'terminated', label: 'Terminated' },
            ]}
            value={status}
            onChange={(v) => setStatus(v as any)}
            onClear={() => setStatus(undefined)}
          />
          <Button action="add" type="primary" onClick={() => navigate('/qaqc/piping/tools/inspectors/create')}>New Inspector</Button>
        </Space>
        <StandardTable
          rowKey={(r: QaqcPipeInspector) => String(r.id)}
          columns={columns}
          dataSource={filtered}
          loading={isLoading}
          pagination={{ pageSize: 20 }}
          enableExport
        />
      </Space>
    </Card>
  );
}
